import { cloneDeep } from '@apollo/client/utilities';
import { Address, Coordinates } from '../common/address';
import { EntityInput, forCreate, forDelete, forUpdate } from '../common/entity';
import { StockLocation } from './stockLocation';

export class StockLocationInput extends StockLocation implements EntityInput<StockLocation> {
  forCreate(): CreateStockLocationInput {
    return CreateStockLocationInput.from(this, CreateStockLocationInput);
  }

  forUpdate(): UpdateStockLocationInput {
    if (this.address) {
      if (!this.address.addressLine1) (this.address.addressLine1 as any) = null;
      if (!this.address.addressLine2) (this.address.addressLine2 as any) = null;
      if (!this.address.city) (this.address.city as any) = null;
      if (!this.address.coordinates) (this.address.coordinates as any) = null;
      if (!this.address.countryCode) (this.address.countryCode as any) = null;
      if (!this.address.postalCode) (this.address.postalCode as any) = null;
      if (!this.address.region) (this.address.region as any) = null;
    }

    delete (this as any).stockLocationId;

    return UpdateStockLocationInput.from(this, UpdateStockLocationInput);
  }

  forDelete(): DeleteStockLocationInput {
    return DeleteStockLocationInput.from(this, DeleteStockLocationInput);
  }

  validate() {
    if (!this.name) return false;

    return true;
  }

  hasChanges(original: StockLocation | undefined) {
    if (!original) return true;
    if (this.name !== original.name) return true;
    if ((this.identifier || original.identifier) && this.identifier !== original.identifier) return true;
    if (this.mobile !== original.mobile) return true;
    if (this.refillId !== original.refillId) return true;

    if (this.address || original.address) {
      if (this.address?.addressLine1 !== original.address?.addressLine1) return true;
      if (this.address?.addressLine2 !== original.address?.addressLine2) return true;
      if (this.address?.city !== original.address?.city) return true;
      if (this.address?.postalCode !== original.address?.postalCode) return true;
      if (this.address?.region !== original.address?.region) return true;
      if (this.address?.countryCode !== original.address?.countryCode) return true;
      if (this.address?.coordinates || original.address?.coordinates) {
        if (this.address?.coordinates?.latitude !== original.address?.coordinates?.latitude) return true;
        if (this.address?.coordinates?.longitude !== original.address?.coordinates?.longitude) return true;
      }
    }

    return false;
  }

  withName(name: string) {
    this.name = name;
    return cloneDeep(this);
  }

  withIdentifier(identifier: string) {
    this.identifier = identifier;
    return cloneDeep(this);
  }

  withMobile(mobile: boolean) {
    this.mobile = mobile;
    return cloneDeep(this);
  }

  withImage(image?: string) {
    this.image = image;
    return cloneDeep(this);
  }

  withAddress(address: Address) {
    this.address = address;
    return cloneDeep(this);
  }

  withAddressLine1(line: string) {
    if (!this.address) this.address = {};
    this.address.addressLine1 = line;
    return cloneDeep(this);
  }

  withAddressLine2(line: string) {
    if (!this.address) this.address = {};
    this.address.addressLine2 = line;
    return cloneDeep(this);
  }

  withCity(city: string) {
    if (!this.address) this.address = {};
    this.address.city = city;
    return cloneDeep(this);
  }

  withRegion(region: string) {
    if (!this.address) this.address = {};
    this.address.region = region;
    return cloneDeep(this);
  }

  withPostalCode(postalCode: string) {
    if (!this.address) this.address = {};
    this.address.postalCode = postalCode;
    return cloneDeep(this);
  }

  withCountryCode(countryCode?: string) {
    if (!this.address) this.address = {};
    this.address.countryCode = countryCode;
    return cloneDeep(this);
  }

  withCoordinates(coordinates?: Coordinates) {
    if (!this.address) this.address = {};
    this.address.coordinates = coordinates;
    return cloneDeep(this);
  }

  withAdministrator(userId: string) {
    const ids = new Set(this.administrators);
    ids.add(userId);
    this.administrators = [...ids.values()];
    return cloneDeep(this);
  }

  withoutAdministrator(userId: string) {
    const ids = new Set(this.administrators);
    ids.delete(userId);
    this.administrators = [...ids.values()];
    return cloneDeep(this);
  }

  withRefillId(refillId?: string) {
    this.refillId = refillId;
    return cloneDeep(this);
  }
}

export class CreateStockLocationInput extends forCreate(StockLocation) {}

export class UpdateStockLocationInput extends forUpdate(StockLocation) {}

export class DeleteStockLocationInput extends forDelete(StockLocation) {}
